import React from "react";
import styles from "../SubjectSinglePage.module.css";
import {
  INLINE_COMMENT_LIMIT,
  INLINE_COMMENT_PREVIEW_COUNT,
} from "../model/constants";
import type { GistItem } from "../model/types";
import {
  buildUsefulDiscussionPreview,
  childrenCountLabel,
  formatDate,
  getCommentAuthor,
  getCommentInitials,
  getCommentText,
  getGistComments,
} from "../model/subjectSingleModel";

type DiscussionPreviewPanelProps = {
  gist: GistItem;
  user: any;
  isComposerActive: boolean;
  isSubmittingComment: boolean;
  submitError?: string;
  inlineCommentDraft: string;
  onOpenComposer: (gistId: number) => void;
  onSubmitComment: (gist: GistItem) => void;
  onCancelComposer: () => void;
  onDraftChange: (value: string) => void;
  onOpenWorkspace: (gist: GistItem) => void;
};

export const DiscussionPreviewPanel: React.FC<DiscussionPreviewPanelProps> = ({
  gist,
  user,
  isComposerActive,
  isSubmittingComment,
  submitError,
  inlineCommentDraft,
  onOpenComposer,
  onSubmitComment,
  onCancelComposer,
  onDraftChange,
  onOpenWorkspace,
}) => {
  const comments = getGistComments(gist);
  const items = buildUsefulDiscussionPreview(comments);
  const totalThreads = comments.filter(
    (comment) =>
      typeof comment.parentId !== "number" ||
      !comments.some((candidate) => candidate.id === comment.parentId),
  ).length;
  const visibleItems = items.slice(0, INLINE_COMMENT_PREVIEW_COUNT);

  return (
    <section
      className={`${styles.discussionPreviewPanel} ${styles.inlineCommentsPreview}`}
      aria-label={`Inline comments preview for ${gist.title}`}
    >
      <div className={styles.discussionPreviewHeader}>
        <div>
          <h3>Comments preview</h3>
          <p>
            <strong>Inline preview</strong>
            <span>Open Comment Workspace for the full threaded comment tools.</span>
          </p>
        </div>
        <div className={styles.discussionPreviewMeta}>
          <span>{comments.length} comments</span>
          <span>{comments.length ? Math.max(1, totalThreads) : 0} threads</span>
        </div>
      </div>

      {items.length ? (
        <div className={styles.discussionCommentList}>
          {visibleItems.map((item) => {
            return (
              <article
                key={item.comment.id}
                className={styles.discussionCommentCard}
                style={{ marginLeft: `${Math.min(item.depth, 3) * 14}px` }}
              >
                <div className={styles.discussionAvatar} aria-hidden="true">
                  {getCommentInitials(item.comment)}
                </div>
                <div className={styles.discussionCommentContent}>
                  <div className={styles.discussionCommentMeta}>
                    <strong>{getCommentAuthor(item.comment)}</strong>
                    {item.isHighlightedPath && <span>Useful path</span>}
                    {item.comment.createdAt && (
                      <time>{formatDate(item.comment.createdAt)}</time>
                    )}
                  </div>
                  <p className={styles.discussionCommentClamped}>
                    {getCommentText(item.comment)}
                  </p>
                  <div className={styles.discussionCommentActions}>
                    <span>{childrenCountLabel(item.comment, comments)}</span>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className={styles.discussionEmptyState}>
          <strong>No comments yet.</strong>
          <span>Add the first comment from the Comment Workspace.</span>
        </div>
      )}

      <div className={styles.discussionPreviewFooter}>
        {items.length > INLINE_COMMENT_PREVIEW_COUNT && (
          <span className={styles.inlineCommentsMore}>
            {items.length - INLINE_COMMENT_PREVIEW_COUNT} more in Comment Workspace
          </span>
        )}
        <button
          type="button"
          className={styles.discussionPrimaryButton}
          onClick={() => onOpenComposer(gist.id)}
        >
          Add comment
        </button>
        <button
          type="button"
          className={styles.discussionSecondaryButton}
          onClick={() => onOpenWorkspace(gist)}
        >
          Open Comment Workspace
        </button>
        <button
          type="button"
          className={styles.discussionSecondaryButton}
          onClick={() => onOpenWorkspace(gist)}
        >
          Expand discussion
        </button>
      </div>
      {isComposerActive && (
        <div className={styles.inlineCommentComposer}>
          <textarea
            value={inlineCommentDraft}
            maxLength={INLINE_COMMENT_LIMIT}
            rows={3}
            disabled={isSubmittingComment}
            onChange={(event) => onDraftChange(event.target.value)}
            placeholder={user ? "Write a comment..." : "Log in to comment"}
            aria-label={`Write a comment on ${gist.title}`}
          />
          <div className={styles.inlineCommentComposerFooter}>
            <span>{inlineCommentDraft.length}/{INLINE_COMMENT_LIMIT}</span>
            <button
              type="button"
              onClick={() => onSubmitComment(gist)}
              disabled={isSubmittingComment || !user || !inlineCommentDraft.trim()}
            >
              {isSubmittingComment ? "Submitting..." : "Submit"}
            </button>
            <button
              type="button"
              onClick={onCancelComposer}
              disabled={isSubmittingComment}
            >
              Cancel
            </button>
          </div>
          {submitError && (
            <div className={styles.inlineCommentError}>{submitError}</div>
          )}
        </div>
      )}
    </section>
  );
};
